import { Pipe, PipeTransform } from '@angular/core';
import { digger } from '@cbre/digger';
import { MIQQLParser } from '@cbre/miqql-parser';
import { Column, hasReadPath } from '@miq/catalog';
import { getGlobalDateDisplayFormat } from '@miq/platform';
import { DataObject, getLovValueKey } from '@miq/shared-models';
import { boolToString, formatDate, formatNumber, MiqMoment, simpleValueValidation } from '@miq/utils';

/**
 * L2-R7: moved from `src/app/shared/pipe/data-cell-formatter.pipe.ts`.
 *
 * This was the single biggest remaining template coupling between the reports remote and the host:
 * 16 reports/pdf-view files imported it and ~26 template bindings use `| dataCellFormatter`, so the
 * remote bundled its own copy of the pipe AND of everything it drags in. It is stateless (no
 * fields, `extractValue` is static), so this is a bundle-size/boundary fix rather than a
 * state-forking fix.
 *
 * Its former host-only dependencies are now all lib code:
 *   - `getLovValueKey`         -> @miq/shared-models (moved in this batch)
 *   - `simpleValueValidation`  -> @miq/utils         (already there; the host path was a shim)
 *   - `getGlobalDateDisplayFormat` -> @miq/platform  (already there; the host path was a shim)
 *   - `DataObject`             -> @miq/shared-models (already there; the host path was a shim)
 */
export const dateSanitizer = (value: string, toUtc?: boolean, extended?: boolean, customFormat?: string) => {
  const isValid = value != null && value !== '' && value !== 'Invalid date';
  const extendedFormat = extended ? (customFormat ? customFormat : 'MM/DD/YYYY h:mm:ss a') : 'MM/DD/YYYY';
  let result = '';
  if (isValid) {
    const formattedDate = MiqMoment.formatFromInputFormat(value, MiqMoment.utcInputFormat, `${extendedFormat}`, toUtc);
    const outputFormat = getGlobalDateDisplayFormat(undefined, extended);
    result = customFormat ? (formattedDate as string) : (formatDate(formattedDate, outputFormat, 'en-US') as string);
  }
  return result;
};
const SANITIZER_MAP_BY_DATA_TYPE = {
  boolean: (value, opts?) => (opts.values ? value : boolToString(value)),
  integer: (value, opts?) =>
    formatNumber(value, 'en-US', !!opts.options && !!opts.options.format ? opts.options.format : undefined),
  numeric: (value, opts?) =>
    formatNumber(value, 'en-US', !!opts.options && !!opts.options.format ? opts.options.format : undefined),
};
const SANITIZER_MAP_BY_INPUT_TYPE = {
  Date: (value, opts?) =>
    dateSanitizer(value, opts.dataType !== 'timestamp with time zone', opts.extended, opts.customFormat),
};
const self = s => s;
const sanitizeValue = (value, { dataType, inputType, values, options, extended, customFormat }: any) => {
  const sanitizer = SANITIZER_MAP_BY_INPUT_TYPE[inputType] || SANITIZER_MAP_BY_DATA_TYPE[dataType] || self;
  return sanitizer(value, { dataType, values, options, extended, customFormat });
};
/**
 * @description
 *
 * If provided, runs the display function, which shall return a value to be
 * displayed. Otherwise, it will return the result of the
 * [digger]{@link digger} helper.
 *
 * @param data any contains the data to be transformed.
 * @param field {string} used to extract data using digger.
 * @param rawData {boolean} determines whether just to extract data or to sanitize also.
 * @param condition {string} applies the condition while extracting the data
 * @param column {object} contains all other fields like inputType, dataType etc.
 */
@Pipe({
  name: 'dataCellFormatter',
  standalone: true,
})
export class DataCellFormatterPipe implements PipeTransform {
  constructor() {}
  public static extractValue<T extends Column = Column>(
    data: any,
    column: Partial<T> & {
      rawData?: boolean;
      filterDuplicates?: boolean;
      displayAllValues?: boolean;
      separator?: string;
      extended?: boolean;
      customFormat?: string;
    },
    lovValueFormatter?: (lovVal: DataObject) => string
  ): any {
    const {
      field = '',
      rawData = false,
      filterDuplicates = true,
      displayAllValues = false,
      condition,
      separator = ' | ',
    } = column;
    let extractedData;
    if (!column.path && simpleValueValidation(condition)) {
      let parsedCondition;
      try {
        parsedCondition = MIQQLParser.parse(condition);
        extractedData = digger(data, field, { condition: parsedCondition, useJsonata: false });
      } catch (e) {
        if (parsedCondition != null) {
          console.error('Something went wrong while extracting data from field with condition');
          extractedData = digger(data, field);
        } else {
          console.error('Something went wrong while parsing the catalog condition for field');
        }
      }
    } else {
      // prevent failure of unimplemented ColumnFactory
      if (!column.getReadPath) {
        column = Object.assign(column, hasReadPath());
      }
      extractedData = digger(data, column.getReadPath());
    }
    if (extractedData != null && column.values && column.values.length > 0) {
      const dataKey = getLovValueKey(column);
      const value = (
        displayAllValues && Array.isArray(extractedData)
          ? extractedData
              .map(value => column.values?.filter(v => v[dataKey] === value)?.[0] || { value: '' })
              .filter(val => !!val)
          : column.values.filter(
              val =>
                val[dataKey] != null &&
                (Array.isArray(extractedData)
                  ? extractedData.some(v => v === val[dataKey])
                  : extractedData === val[dataKey])
            )
      ).map(val => (lovValueFormatter ? lovValueFormatter(val) : val.value));
      if (value.length > 0) {
        extractedData = value;
      }
    }
    if (extractedData != null) {
      if (!rawData) {
        if (Array.isArray(extractedData)) {
          if (filterDuplicates) {
            return Array.from(
              new Set(
                extractedData.filter(value => value != null && value !== '').map(value => sanitizeValue(value, column))
              )
            )
              .sort((a, b) => {
                // Convert to strings before calling localeCompare
                const strA = String(a || '');
                const strB = String(b || '');
                return strA.localeCompare(strB);
              })
              .join(separator);
          }
          return Array.from(extractedData, value => sanitizeValue(value, column)).join(separator);
        }
        return sanitizeValue(extractedData, column).toString().trim();
      }
      return extractedData;
    }
    return '';
  }
  /**
   * @param data object to get data from.
   * @param args special arguments.
   * @param _updated optional flag used to manually trigger the pipe.
   */
  transform(
    data: DataObject,
    args: Partial<
      Column & { settings?: any; mappingLOV?: Array<{ [key: string]: any }>; extended?: boolean; customFormat?: string }
    > = {},
    _updated?: boolean
  ): any {
    const { displayFn, settings = {}, mappingLOV } = args;
    try {
      if (displayFn && typeof displayFn === 'function') {
        let result = displayFn(data, settings, mappingLOV);
        if (result && typeof result === 'object' && result.value != null) {
          result = result.value;
        }
        return result;
      }
      return DataCellFormatterPipe.extractValue(data, args);
    } catch (e) {
      // this catch will keep ppt download working in case any field is error
      return '';
    }
  }
}
